import { createSlice, createAsyncThunk, createSelector } from "@reduxjs/toolkit";
import {
  listInboxItems,
  getInboxUnreadCount,
  markInboxReviewed,
  markInboxUnread,
  snoozeInboxItem,
  unsnoozeInboxItem,
  listInboxTasks,
  createInboxTask,
  updateInboxTaskStatus,
  listInboxMessages,
  createInboxMessage,
  markInboxMessageRead,
  listDeadlineCards,
  confirmDeadlineCard,
  dismissDeadlineCard,
  dismissInboxDuplicate,
  restoreInboxDuplicate,
} from "../service/inbox";

/**
 * Attorney Inbox slice.
 *
 * Mirrors the BE handoff at `dms_backend/.cursor/agent-handoff-phase5-attorney-
 * inbox.md` and contracts at `dms_backend/documents/views/inbox/`.
 *
 * State shape
 * -----------
 *  list:
 *    status:     "idle" | "loading" | "ready" | "failed"
 *    items:      InboxItem[]                (current page only — append on
 *                                            page 2+ if we ever switch from
 *                                            paged to infinite scroll)
 *    count:      number                     (total across all pages, server-
 *                                            authoritative)
 *    next/previous: string | null           (raw DRF page URLs)
 *    page:       number                     (1-indexed)
 *    pageSize:   number
 *    matterId:   number | null              (active matter filter, if any)
 *    includeSnoozed: boolean
 *    error:      string | null
 *
 *  unreadCount:
 *    status:  "idle" | "loading" | "ready" | "failed"
 *    value:   number                        (last known badge count)
 *    error:   string | null
 *
 *  selectedDocumentId: number | null        (driven by URL — `/inbox/<id>`.
 *                                            Slice mirrors the URL value so
 *                                            cross-component reads stay cheap.)
 *
 *  mutations: { [documentId]: { kind: status } }
 *    Per-doc in-flight flags for the action footer's button state. Keys
 *    are coarse: "readState" (mark-reviewed/unread), "snooze", "task"
 *    (create), "message" (create). A null/missing entry means idle. We
 *    avoid `Set`/`Map` so it stays JSON-serializable and Redux DevTools-
 *    friendly.
 *
 *  tasksByDocument:    { [documentId]: { status, items, error } }
 *  messagesByDocument: { [documentId]: { status, items, error } }
 *    Lazy-fetched right rail caches. Hydrated on first open of the
 *    Tasks / Messages pane. Mutations (create / status PATCH / mark-read)
 *    patch these caches in place so the UI reflects the change without
 *    a roundtrip refetch.
 *
 *  taskItemMutations:    { [taskId]: "loading" | undefined }
 *  messageItemMutations: { [messageId]: "loading" | undefined }
 *    Per-entity in-flight flags for item-level mutations: task status
 *    PATCH and message mark-read. Distinct from `mutations` (which is
 *    keyed by documentId for footer-level actions like create-task /
 *    create-message) so a status PATCH on task #5 doesn't gray out the
 *    Mark-read button on message #9 even when both belong to the same
 *    document.
 *
 * Why a separate slice (vs. extending AISlice)
 * --------------------------------------------
 * The inbox list is a top-level page surface, not part of the AI panel
 * lifecycle. AISlice already holds per-document briefings / jobs /
 * reports / context-analysis state — keeping inbox-level pagination +
 * filter + selection state separate prevents coupling between
 * "current document AI artifacts" and "list of incoming docs."
 */

const initialListState = {
  status: "idle",
  items: [],
  count: 0,
  next: null,
  previous: null,
  page: 1,
  pageSize: 20,
  matterId: null,
  includeSnoozed: false,
  recentlyReviewed: false,
  category: null,
  categoryCounts: [],
  error: null,
};

const initialUnreadCountState = {
  status: "idle",
  value: 0,
  error: null,
};

const initialDeadlineCardsState = {
  status: "idle",
  items: [],
  total: 0,
  error: null,
};

const initialState = {
  list: initialListState,
  unreadCount: initialUnreadCountState,
  selectedDocumentId: null,
  mutations: {},
  tasksByDocument: {},
  messagesByDocument: {},
  taskItemMutations: {},
  messageItemMutations: {},
  // Proposed-deadline cards (user-level queue, not per-document —
  // fetched on inbox mount + doc-param changes; confirm/dismiss
  // remove the card from the actionable list in place). See
  // deadlineCardMutations for the per-card in-flight flags the
  // strip's buttons read. resolvedDeadlineCardIds remembers cards
  // confirmed/dismissed this session so a fetch response that was
  // already in flight when the user acted can't write the card back
  // into the strip (Bugbot #304).
  deadlineCards: initialDeadlineCardsState,
  deadlineCardMutations: {},
  resolvedDeadlineCardIds: [],
};

const FROZEN_EMPTY_TASK_SLOT = Object.freeze({
  status: "idle",
  items: [],
  error: null,
});

const FROZEN_EMPTY_MESSAGE_SLOT = Object.freeze({
  status: "idle",
  items: [],
  error: null,
});

/**
 * Patch a single row in the current `list.items` page. Used by mutation
 * fulfilled reducers to reflect server state (is_read, snoozed_until,
 * open_task_count, unread_message_count) without a list refetch round
 * trip. Silently skips if the row isn't on the current page (rare —
 * the user generally has the row visible when mutating it).
 */
const patchListItem = (state, documentId, patch) => {
  if (documentId == null || !patch) return;
  const idx = state.list.items.findIndex(
    (row) => String(row?.id) === String(documentId)
  );
  if (idx < 0) return;
  state.list.items[idx] = { ...state.list.items[idx], ...patch };
};

const removeListItem = (state, documentId) => {
  const before = state.list.items.length;
  state.list.items = state.list.items.filter(
    (row) => String(row?.id) !== String(documentId)
  );
  if (state.list.items.length === before) return false;
  state.list.count = Math.max(0, state.list.count - 1);
  return true;
};

/**
 * Bump the unread badge by `delta` (clamped to ≥ 0). Mutations that
 * change read state echo a delta locally so the badge updates instantly,
 * then the post-mutation `fetchInboxUnreadCount` reconciliation in the
 * thunk pipeline corrects any drift on the next tick.
 */
const adjustUnreadCount = (state, delta) => {
  const next = (state.unreadCount.value || 0) + delta;
  state.unreadCount.value = Math.max(0, next);
};

const setMutationStatus = (state, documentId, kind, status) => {
  const key = documentId == null ? "__standalone__" : String(documentId);
  if (status == null) {
    if (state.mutations[key]) {
      delete state.mutations[key][kind];
      if (!Object.keys(state.mutations[key]).length) {
        delete state.mutations[key];
      }
    }
    return;
  }
  if (!state.mutations[key]) state.mutations[key] = {};
  state.mutations[key][kind] = status;
};

/**
 * Per-entity (task / message) in-flight flag. Phase 5c's right-rail
 * panels need item-level busy state — a task status PATCH must not
 * disable the Mark-read button on a message that happens to belong
 * to the same document. Keys are scalar entity IDs so callers can
 * read with a single lookup.
 */
const setItemMutationStatus = (bag, id, status) => {
  if (id == null) return;
  const key = String(id);
  if (status == null) {
    delete bag[key];
    return;
  }
  bag[key] = status;
};

/**
 * Remember a confirmed/dismissed deadline card so a fetch response
 * that was already in flight when the user acted can't write the card
 * back into the strip. Bounded by the session's action count (a human
 * confirms a handful of deadlines per day) and server-authoritative
 * anyway — the card leaves `status=proposed` on the BE, so later
 * fetches exclude it regardless.
 */
const rememberResolvedDeadlineCard = (state, cardId) => {
  if (cardId == null) return;
  if (!Array.isArray(state.resolvedDeadlineCardIds)) {
    state.resolvedDeadlineCardIds = [];
  }
  const key = String(cardId);
  if (!state.resolvedDeadlineCardIds.includes(key)) {
    state.resolvedDeadlineCardIds.push(key);
  }
};

/**
 * Drop an actioned card from the strip and shrink the queue total with
 * it. The total counts the WHOLE server-side queue, not just the page
 * on screen, so it has to be decremented here — recomputing it from
 * `items.length` would collapse it back to the page size.
 */
const removeDeadlineCard = (state, cardId) => {
  const before = state.deadlineCards.items.length;
  state.deadlineCards.items = state.deadlineCards.items.filter(
    (card) => String(card.id) !== String(cardId)
  );
  const removed = before - state.deadlineCards.items.length;
  if (removed > 0) {
    state.deadlineCards.total = Math.max(
      (state.deadlineCards.total ?? before) - removed,
      state.deadlineCards.items.length
    );
  }
};

// ---------------------------------------------------------------------------
// Thunks
// ---------------------------------------------------------------------------

/**
 * Fetch a page of inbox items.
 *
 * Args (all optional, all map to BE query params):
 *   matterId         → ?matter_id
 *   includeSnoozed   → ?include_snoozed
 *   recentlyReviewed → ?reviewed_only (the reviewed pile, newest
 *                      review first, last two weeks only)
 *   page             → ?page  (1-indexed)
 *   pageSize         → ?page_size
 *
 * The reducer treats this as a REPLACE (not APPEND) — paginated lists are
 * page-at-a-time today; if/when we switch to infinite scroll we'll add a
 * separate `loadMoreInboxItems` thunk to make the append intent explicit.
 */
export const fetchInboxItems = createAsyncThunk(
  "inbox/fetchInboxItems",
  async (
    {
      matterId = null,
      includeSnoozed = false,
      recentlyReviewed = false,
      category = null,
      page = 1,
      pageSize = 20,
    } = {},
    { rejectWithValue }
  ) => {
    try {
      const response = await listInboxItems({
        matterId,
        includeSnoozed,
        recentlyReviewed,
        category,
        page,
        pageSize,
      });
      const data = response?.data || {};
      return {
        items: Array.isArray(data.results) ? data.results : [],
        count: typeof data.count === "number" ? data.count : 0,
        next: data.next || null,
        previous: data.previous || null,
        page,
        pageSize,
        matterId,
        includeSnoozed,
        recentlyReviewed,
        category,
        categoryCounts: Array.isArray(data.category_counts)
          ? data.category_counts
          : [],
      };
    } catch (err) {
      return rejectWithValue({
        message:
          err?.response?.data?.detail ||
          err?.message ||
          "Failed to load inbox items",
        status: err?.response?.status,
      });
    }
  }
);

/**
 * Fetch the unread badge count. Cheap (single int payload), driven from
 * the sidebar mount + a periodic refresh + post-mutation invalidations
 * (Phase 5b — after mark-reviewed, mark-unread, snooze, etc).
 */
export const fetchInboxUnreadCount = createAsyncThunk(
  "inbox/fetchInboxUnreadCount",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getInboxUnreadCount();
      const value = response?.data?.unread_count;
      return typeof value === "number" ? value : 0;
    } catch (err) {
      return rejectWithValue({
        message:
          err?.response?.data?.detail ||
          err?.message ||
          "Failed to load unread count",
        status: err?.response?.status,
      });
    }
  }
);

// ---------------------------------------------------------------------------
// Mutation thunks (Phase 5b — write surface)
// ---------------------------------------------------------------------------

/**
 * Common rejection shape so every mutation reducer can pull `.message`
 * + `.status` from `action.payload` consistently.
 */
const rejectionFromAxiosError = (err, fallback) => ({
  message:
    err?.response?.data?.detail ||
    err?.response?.data?.body ||
    err?.response?.data?.assigned_to_user_id ||
    err?.response?.data?.recipient_user_id ||
    err?.response?.data?.cc_user_ids ||
    err?.response?.data?.add_participant_ids ||
    err?.response?.data?.until ||
    err?.message ||
    fallback,
  status: err?.response?.status,
});

/**
 * Mark a document reviewed for the current user. Fulfilled reducer
 * patches the row's `is_read` flag and decrements the unread badge.
 * Idempotent — calling twice has the same effect as calling once.
 */
export const markInboxItemReviewed = createAsyncThunk(
  "inbox/markReviewed",
  async ({ documentId, propagate } = {}, { rejectWithValue }) => {
    try {
      // Only the "Keep in Chris's mailbox" answer changes the request;
      // everything else is the plain call the rest of the app makes.
      const response =
        propagate === false
          ? await markInboxReviewed(documentId, { propagate: false })
          : await markInboxReviewed(documentId);
      const data = response?.data || {};
      return {
        documentId,
        is_read: true,
        read_at: data.read_at || new Date().toISOString(),
      };
    } catch (err) {
      return rejectWithValue({
        ...rejectionFromAxiosError(err, "Failed to mark reviewed"),
        documentId,
      });
    }
  }
);

/**
 * Inverse of mark-reviewed. Fulfilled reducer increments the unread
 * badge and clears `is_read`.
 */
export const markInboxItemUnread = createAsyncThunk(
  "inbox/markUnread",
  async ({ documentId } = {}, { rejectWithValue }) => {
    try {
      await markInboxUnread(documentId);
      return { documentId, is_read: false, read_at: null };
    } catch (err) {
      return rejectWithValue({
        ...rejectionFromAxiosError(err, "Failed to mark unread"),
        documentId,
      });
    }
  }
);

/**
 * Snooze a document. `payload` is forwarded to the BE as-is — pass
 * either `{ until: <ISO> }` (preset menu + custom picker path) or
 * `{ duration_days: <n> }` (hypothetical command-bar shorthand). The
 * BE rejects non-future timestamps with 400.
 *
 * Fulfilled reducer patches the row's `snoozed_until`. An active snooze is
 * removed immediately when the default inbox view excludes snoozed items;
 * the "show snoozed" view keeps the row visible.
 */
export const snoozeInboxItemThunk = createAsyncThunk(
  "inbox/snooze",
  async ({ documentId, ...payload } = {}, { rejectWithValue }) => {
    try {
      const response = await snoozeInboxItem(documentId, payload);
      const data = response?.data || {};
      return {
        documentId,
        snoozed_until: data.snoozed_until || null,
      };
    } catch (err) {
      return rejectWithValue({
        ...rejectionFromAxiosError(err, "Failed to snooze"),
        documentId,
      });
    }
  }
);

/**
 * Unsnooze (remove the active snooze row). Fulfilled reducer clears
 * `snoozed_until` on the matching list row.
 */
export const unsnoozeInboxItemThunk = createAsyncThunk(
  "inbox/unsnooze",
  async ({ documentId } = {}, { rejectWithValue }) => {
    try {
      await unsnoozeInboxItem(documentId);
      return { documentId, snoozed_until: null };
    } catch (err) {
      return rejectWithValue({
        ...rejectionFromAxiosError(err, "Failed to unsnooze"),
        documentId,
      });
    }
  }
);

/**
 * Lazy-fetch the task list for a document. Fired on first open of the
 * action footer's "Tasks" affordance (or any UI that needs the full
 * list — the row's `open_task_count` is a count-only signal, not a
 * cache).
 */
export const fetchInboxTasks = createAsyncThunk(
  "inbox/fetchTasks",
  async ({ documentId } = {}, { rejectWithValue }) => {
    try {
      const response = await listInboxTasks(documentId);
      const data = response?.data;
      return {
        documentId,
        items: Array.isArray(data) ? data : data?.results || [],
      };
    } catch (err) {
      return rejectWithValue({
        ...rejectionFromAxiosError(err, "Failed to load tasks"),
        documentId,
      });
    }
  }
);

/**
 * Create a new InboxTask. Fulfilled reducer prepends the new task to
 * the per-doc cache when a source file was named, and bumps the row's
 * `open_task_count`. The assignee gets a notification.
 *
 * Source file and matter are both optional (Matthew, 2026-08-18): a
 * Task can hang off a document, belong only to a case, or stand alone.
 * Urgent still raises the alert banner; everything else stays in the
 * bell. `kind` is accepted from older callers and ignored — new work
 * is always a Task.
 */
export const createInboxTaskThunk = createAsyncThunk(
  "inbox/createTask",
  async (
    { documentId, matterId, assignedToUserId, body, kind, dueDate, priority } = {},
    { rejectWithValue }
  ) => {
    try {
      const response = await createInboxTask(documentId, {
        assigned_to_user_id: assignedToUserId,
        body,
        ...(kind ? { kind } : {}),
        ...(dueDate ? { due_date: dueDate } : {}),
        ...(priority ? { priority } : {}),
        ...(matterId ? { matter_id: matterId } : {}),
      });
      return { documentId, task: response?.data || null };
    } catch (err) {
      return rejectWithValue({
        ...rejectionFromAxiosError(err, "Failed to create task"),
        documentId,
      });
    }
  }
);

/**
 * Update task status. Fulfilled reducer patches the matching task in
 * the per-doc cache and adjusts the row's `open_task_count` if the
 * transition crosses the open/closed boundary.
 */
export const updateInboxTaskStatusThunk = createAsyncThunk(
  "inbox/updateTaskStatus",
  async ({ documentId, taskId, status } = {}, { rejectWithValue }) => {
    try {
      const response = await updateInboxTaskStatus(taskId, status);
      return { documentId, task: response?.data || null };
    } catch (err) {
      return rejectWithValue({
        ...rejectionFromAxiosError(err, "Failed to update task"),
        documentId,
        taskId,
      });
    }
  }
);

/**
 * Lazy-fetch the message list for a document.
 */
export const fetchInboxMessages = createAsyncThunk(
  "inbox/fetchMessages",
  async ({ documentId } = {}, { rejectWithValue }) => {
    try {
      const response = await listInboxMessages(documentId);
      const data = response?.data;
      return {
        documentId,
        items: Array.isArray(data) ? data : data?.results || [],
      };
    } catch (err) {
      return rejectWithValue({
        ...rejectionFromAxiosError(err, "Failed to load messages"),
        documentId,
      });
    }
  }
);

/**
 * Create a new InboxMessage. Fulfilled reducer prepends the new
 * message to the per-doc cache. (No row-count bump — the recipient's
 * unread_message_count increments on their next list fetch; this
 * matters in v1 because messages are person-to-person, so the sender's
 * UI doesn't reflect unread state.)
 */
export const createInboxMessageThunk = createAsyncThunk(
  "inbox/createMessage",
  async (
    { documentId, recipientUserId, body, ccUserIds } = {},
    { rejectWithValue }
  ) => {
    try {
      const response = await createInboxMessage(documentId, {
        recipient_user_id: recipientUserId,
        body,
        cc_user_ids: ccUserIds,
      });
      return { documentId, message: response?.data || null };
    } catch (err) {
      return rejectWithValue({
        ...rejectionFromAxiosError(err, "Failed to send message"),
        documentId,
      });
    }
  }
);

/**
 * Mark a message read on behalf of the current user (recipient).
 * Fulfilled reducer patches the message's `read_at` and decrements
 * the row's `unread_message_count`. BE 403s if the caller isn't the
 * recipient — surfaced as a rejected action.
 */
export const markInboxMessageReadThunk = createAsyncThunk(
  "inbox/markMessageRead",
  async ({ documentId, messageId } = {}, { rejectWithValue }) => {
    try {
      const response = await markInboxMessageRead(messageId);
      return { documentId, message: response?.data || null };
    } catch (err) {
      return rejectWithValue({
        ...rejectionFromAxiosError(err, "Failed to mark message read"),
        documentId,
        messageId,
      });
    }
  }
);

/**
 * Fetch the user's proposed deadline cards (deadline calendaring).
 * One user-level list — fetched on inbox mount alongside the item
 * list, refreshed after confirm/dismiss only via the in-place
 * reducer patch (the BE list is capped and soonest-deadline-first, so
 * a full refetch on every action would churn for no benefit).
 *
 * `total` is the BE's count of the WHOLE queue, which can exceed the
 * page it returned. Older builds counted the rows they received and
 * reported the cap as the queue size, so a 709-deadline queue read as
 * "100" — the header must never understate a statutory backlog.
 */
export const fetchDeadlineCards = createAsyncThunk(
  "inbox/fetchDeadlineCards",
  async ({ status = "proposed", matterId } = {}, { rejectWithValue }) => {
    try {
      const response = await listDeadlineCards({ status, matterId });
      const items = response?.data?.results || [];
      return {
        items,
        total:
          typeof response?.data?.total === "number"
            ? response.data.total
            : items.length,
      };
    } catch (err) {
      return rejectWithValue(
        rejectionFromAxiosError(err, "Failed to load deadline cards")
      );
    }
  }
);

/**
 * [Create event] — confirm a deadline card. `deadlineDate` (YYYY-MM-DD)
 * is required when the card has no computed deadline and optional as
 * an override otherwise. Fulfilled reducer swaps the confirmed card
 * out of the proposed strip; the Salesforce Event mirrors after
 * commit on the BE.
 */
export const confirmDeadlineCardThunk = createAsyncThunk(
  "inbox/confirmDeadlineCard",
  async ({ cardId, deadlineDate } = {}, { rejectWithValue }) => {
    try {
      const response = await confirmDeadlineCard(cardId, { deadlineDate });
      return { cardId, card: response?.data || null };
    } catch (err) {
      return rejectWithValue({
        ...rejectionFromAxiosError(err, "Failed to create the event"),
        cardId,
      });
    }
  }
);

/**
 * [Dismiss] — refuse a deadline card with a mandatory reason
 * (`already_calendared` / `not_rating_decision` / `duplicate` /
 * `other` + note). Fulfilled reducer removes the card from the strip.
 */
export const dismissDeadlineCardThunk = createAsyncThunk(
  "inbox/dismissDeadlineCard",
  async ({ cardId, reason, note } = {}, { rejectWithValue }) => {
    try {
      const response = await dismissDeadlineCard(cardId, { reason, note });
      return { cardId, card: response?.data || null };
    } catch (err) {
      return rejectWithValue({
        ...rejectionFromAxiosError(err, "Failed to dismiss the deadline"),
        cardId,
      });
    }
  }
);

/**
 * [Dismiss] — "this is not a copy of that". Clears the duplicate flag
 * from the row.
 *
 * The dismissal is shared across the team rather than stored per user,
 * so this is a statement about the two documents and not a personal
 * preference. `restoreInboxDuplicateThunk` is its undo, which is why
 * the banner offers one.
 */
export const dismissInboxDuplicateThunk = createAsyncThunk(
  "inbox/dismissDuplicate",
  async ({ documentId, matchId } = {}, { rejectWithValue }) => {
    try {
      const response = await dismissInboxDuplicate(documentId, matchId);
      return { documentId, matchId: response?.data?.match_id ?? matchId };
    } catch (err) {
      return rejectWithValue({
        ...rejectionFromAxiosError(err, "Couldn't dismiss the duplicate flag"),
        documentId,
      });
    }
  }
);

/** [Restore] — undo a dismissal, putting the flag back on the row. */
export const restoreInboxDuplicateThunk = createAsyncThunk(
  "inbox/restoreDuplicate",
  async ({ documentId, matchId } = {}, { rejectWithValue }) => {
    try {
      const response = await restoreInboxDuplicate(documentId, matchId);
      return {
        documentId,
        duplicateFlag: response?.data?.duplicate_flag || null,
      };
    } catch (err) {
      return rejectWithValue({
        ...rejectionFromAxiosError(err, "Couldn't restore the duplicate flag"),
        documentId,
      });
    }
  }
);

// ---------------------------------------------------------------------------
// Slice
// ---------------------------------------------------------------------------

const inboxSlice = createSlice({
  name: "inbox",
  initialState,
  reducers: {
    /**
     * Mirror the URL's selected document into Redux so non-route
     * components (right rail header, document viewer) can read it
     * without prop-drilling. Set to null when the user is on
     * `/inbox` with no doc selected.
     */
    setSelectedInboxDocument: (state, action) => {
      // Keep the id as a STRING — post split-schema, document ids are
      // UUIDs ("6320d095-…"), and Number() coerced them to NaN, so the
      // selected-item lookup never matched and the right rail showed
      // "Untitled document / Not yet assigned" for a selected row
      // (firehose day one, 2026-07-09; same bug class as FE #195).
      const value = action.payload;
      state.selectedDocumentId =
        value == null || value === "" ? null : String(value);
    },

    /**
     * Drop the cached list (used on inbox page unmount or when the
     * user explicitly resets filters). Unread count survives — the
     * sidebar badge needs to render across page transitions, and
     * the value is server-authoritative anyway. Per-doc task /
     * message caches also survive — they're keyed by document id and
     * stay valid for as long as the doc exists; clearing them on a
     * filter reset would force unnecessary refetches.
     */
    clearInboxList: (state) => {
      state.list = initialListState;
      state.selectedDocumentId = null;
    },

    /**
     * Drop the per-doc task / message caches for a single document
     * (used on doc unmount in the right rail to keep memory bounded
     * across long inbox sessions). Mutations in flight are NOT
     * cancelled — they'll complete and patch a slot that's gone, but
     * the patchListItem helper silently skips off-page rows so this
     * is safe.
     */
    clearInboxDocumentCaches: (state, action) => {
      const documentId = action.payload;
      if (documentId == null) return;
      const key = String(documentId);
      delete state.tasksByDocument[key];
      delete state.messagesByDocument[key];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchInboxItems.pending, (state) => {
        state.list.status = "loading";
        state.list.error = null;
      })
      .addCase(fetchInboxItems.fulfilled, (state, action) => {
        const {
          items,
          count,
          next,
          previous,
          page,
          pageSize,
          matterId,
          includeSnoozed,
          recentlyReviewed,
          category,
          categoryCounts,
        } = action.payload || {};
        state.list = {
          status: "ready",
          items: Array.isArray(items) ? items : [],
          count: count || 0,
          next: next || null,
          previous: previous || null,
          page: page || 1,
          pageSize: pageSize || 20,
          matterId: matterId ?? null,
          includeSnoozed: Boolean(includeSnoozed),
          recentlyReviewed: Boolean(recentlyReviewed),
          category: category || null,
          categoryCounts: Array.isArray(categoryCounts) ? categoryCounts : [],
          error: null,
        };
      })
      .addCase(fetchInboxItems.rejected, (state, action) => {
        // Preserve the previous items list on transient failures so the
        // user doesn't lose their place if the network blips during a
        // background refresh. The error banner makes the failure visible.
        state.list.status = "failed";
        state.list.error =
          action.payload?.message || "Failed to load inbox items";
      })
      .addCase(fetchInboxUnreadCount.pending, (state) => {
        state.unreadCount.status = "loading";
        state.unreadCount.error = null;
      })
      .addCase(fetchInboxUnreadCount.fulfilled, (state, action) => {
        state.unreadCount = {
          status: "ready",
          value: typeof action.payload === "number" ? action.payload : 0,
          error: null,
        };
      })
      .addCase(fetchInboxUnreadCount.rejected, (state, action) => {
        state.unreadCount.status = "failed";
        // Keep the last-known value so the badge doesn't flicker to 0
        // on a transient failure.
        state.unreadCount.error =
          action.payload?.message || "Failed to load unread count";
      })
      // ---------------------------------------------------------------------
      // Mark reviewed / unread (Phase 5b)
      // ---------------------------------------------------------------------
      .addCase(markInboxItemReviewed.pending, (state, action) => {
        setMutationStatus(state, action.meta.arg?.documentId, "readState", "loading");
      })
      .addCase(markInboxItemReviewed.fulfilled, (state, action) => {
        const { documentId, is_read, read_at } = action.payload || {};
        const wasUnread = (() => {
          const row = state.list.items.find(
            (r) => String(r?.id) === String(documentId)
          );
          return row ? !row.is_read : false;
        })();
        if (state.list.recentlyReviewed) {
          // "Recently reviewed" mode: the row belongs in this pile, so
          // it stays visible and picks up the review time. Its position
          // only moves on the next refetch — resorting the page under
          // the attorney's cursor would be worse than a stale order.
          patchListItem(state, documentId, {
            is_read,
            read_at,
            reviewed_at: read_at,
          });
        } else {
          // Default mode: reviewed documents leave the inbox NOW
          // (Matthew, 2026-07-17) — not on the next refetch.
          const before = state.list.items.length;
          state.list.items = state.list.items.filter(
            (r) => String(r?.id) !== String(documentId)
          );
          if (state.list.items.length !== before) {
            state.list.count = Math.max(0, state.list.count - 1);
          }
        }
        if (wasUnread) adjustUnreadCount(state, -1);
        setMutationStatus(state, documentId, "readState", null);
      })
      .addCase(markInboxItemReviewed.rejected, (state, action) => {
        setMutationStatus(state, action.payload?.documentId, "readState", null);
      })
      .addCase(markInboxItemUnread.pending, (state, action) => {
        setMutationStatus(state, action.meta.arg?.documentId, "readState", "loading");
      })
      .addCase(markInboxItemUnread.fulfilled, (state, action) => {
        const { documentId, is_read, read_at } = action.payload || {};
        const wasRead = (() => {
          const row = state.list.items.find(
            (r) => String(r?.id) === String(documentId)
          );
          return row ? Boolean(row.is_read) : false;
        })();
        if (state.list.recentlyReviewed) {
          // Mirror of mark-reviewed in the default pile: a document put
          // back on the to-do list is no longer part of "what I have
          // reviewed", so it leaves this list immediately.
          removeListItem(state, documentId);
        } else {
          patchListItem(state, documentId, { is_read, read_at });
        }
        if (wasRead) adjustUnreadCount(state, +1);
        setMutationStatus(state, documentId, "readState", null);
      })
      .addCase(markInboxItemUnread.rejected, (state, action) => {
        setMutationStatus(state, action.payload?.documentId, "readState", null);
      })
      // ---------------------------------------------------------------------
      // Snooze / unsnooze (Phase 5b)
      // ---------------------------------------------------------------------
      .addCase(snoozeInboxItemThunk.pending, (state, action) => {
        setMutationStatus(state, action.meta.arg?.documentId, "snooze", "loading");
      })
      .addCase(snoozeInboxItemThunk.fulfilled, (state, action) => {
        const { documentId, snoozed_until } = action.payload || {};
        const snoozeTimestamp = Date.parse(snoozed_until || "");
        const snoozeActive =
          Number.isFinite(snoozeTimestamp) && snoozeTimestamp > Date.now();
        if (!state.list.includeSnoozed && snoozeActive) {
          removeListItem(state, documentId);
        } else {
          patchListItem(state, documentId, { snoozed_until });
        }
        setMutationStatus(state, documentId, "snooze", null);
      })
      .addCase(snoozeInboxItemThunk.rejected, (state, action) => {
        setMutationStatus(state, action.payload?.documentId, "snooze", null);
      })
      .addCase(unsnoozeInboxItemThunk.pending, (state, action) => {
        setMutationStatus(state, action.meta.arg?.documentId, "snooze", "loading");
      })
      .addCase(unsnoozeInboxItemThunk.fulfilled, (state, action) => {
        const { documentId, snoozed_until } = action.payload || {};
        patchListItem(state, documentId, { snoozed_until });
        setMutationStatus(state, documentId, "snooze", null);
      })
      .addCase(unsnoozeInboxItemThunk.rejected, (state, action) => {
        setMutationStatus(state, action.payload?.documentId, "snooze", null);
      })
      // ---------------------------------------------------------------------
      // Tasks (Phase 5b)
      // ---------------------------------------------------------------------
      .addCase(fetchInboxTasks.pending, (state, action) => {
        const key = String(action.meta.arg?.documentId);
        if (!state.tasksByDocument[key]) {
          state.tasksByDocument[key] = { ...FROZEN_EMPTY_TASK_SLOT };
        }
        state.tasksByDocument[key].status = "loading";
        state.tasksByDocument[key].error = null;
      })
      .addCase(fetchInboxTasks.fulfilled, (state, action) => {
        const { documentId, items } = action.payload || {};
        if (documentId == null) return;
        state.tasksByDocument[String(documentId)] = {
          status: "ready",
          items: Array.isArray(items) ? items : [],
          error: null,
        };
      })
      .addCase(fetchInboxTasks.rejected, (state, action) => {
        const documentId = action.payload?.documentId;
        if (documentId == null) return;
        const key = String(documentId);
        if (!state.tasksByDocument[key]) {
          state.tasksByDocument[key] = { ...FROZEN_EMPTY_TASK_SLOT };
        }
        state.tasksByDocument[key].status = "failed";
        state.tasksByDocument[key].error =
          action.payload?.message || "Failed to load tasks";
      })
      .addCase(createInboxTaskThunk.pending, (state, action) => {
        setMutationStatus(state, action.meta.arg?.documentId, "task", "loading");
      })
      .addCase(createInboxTaskThunk.fulfilled, (state, action) => {
        const { documentId, task } = action.payload || {};
        if (documentId != null && documentId !== "__standalone__" && task) {
          const key = String(documentId);
          if (!state.tasksByDocument[key]) {
            state.tasksByDocument[key] = { ...FROZEN_EMPTY_TASK_SLOT };
          }
          state.tasksByDocument[key].items = [task, ...state.tasksByDocument[key].items];
          // Bump the row's open_task_count so the chip in the list
          // reflects the new task immediately. open status is the
          // BE default for newly-created tasks (handoff §2.5) so we
          // can assume +1 without inspecting `task.status`.
          const row = state.list.items.find(
            (r) => String(r?.id) === String(documentId)
          );
          if (row) {
            patchListItem(state, documentId, {
              open_task_count: (row.open_task_count || 0) + 1,
            });
          }
        }
        setMutationStatus(state, documentId, "task", null);
      })
      .addCase(createInboxTaskThunk.rejected, (state, action) => {
        setMutationStatus(state, action.payload?.documentId, "task", null);
      })
      .addCase(updateInboxTaskStatusThunk.pending, (state, action) => {
        setItemMutationStatus(
          state.taskItemMutations,
          action.meta.arg?.taskId,
          "loading"
        );
      })
      .addCase(updateInboxTaskStatusThunk.fulfilled, (state, action) => {
        const { documentId, task } = action.payload || {};
        if (task?.id != null) {
          setItemMutationStatus(state.taskItemMutations, task.id, null);
        }
        if (documentId == null || !task?.id) return;
        const key = String(documentId);
        const slot = state.tasksByDocument[key];
        if (!slot) return;
        const idx = slot.items.findIndex((t) => t?.id === task.id);
        if (idx < 0) return;
        const prev = slot.items[idx];
        slot.items[idx] = task;
        const row = state.list.items.find(
          (r) => String(r?.id) === String(documentId)
        );
        if (row) {
          // Cross the open/closed boundary?
          const wasOpen = prev.status === "open" || prev.status === "in_progress";
          const isOpen = task.status === "open" || task.status === "in_progress";
          if (wasOpen && !isOpen) {
            patchListItem(state, documentId, {
              open_task_count: Math.max(0, (row.open_task_count || 0) - 1),
            });
          } else if (!wasOpen && isOpen) {
            patchListItem(state, documentId, {
              open_task_count: (row.open_task_count || 0) + 1,
            });
          }
        }
      })
      .addCase(updateInboxTaskStatusThunk.rejected, (state, action) => {
        setItemMutationStatus(
          state.taskItemMutations,
          action.payload?.taskId,
          null
        );
      })
      // ---------------------------------------------------------------------
      // Messages (Phase 5b)
      // ---------------------------------------------------------------------
      .addCase(fetchInboxMessages.pending, (state, action) => {
        const key = String(action.meta.arg?.documentId);
        if (!state.messagesByDocument[key]) {
          state.messagesByDocument[key] = { ...FROZEN_EMPTY_MESSAGE_SLOT };
        }
        state.messagesByDocument[key].status = "loading";
        state.messagesByDocument[key].error = null;
      })
      .addCase(fetchInboxMessages.fulfilled, (state, action) => {
        const { documentId, items } = action.payload || {};
        if (documentId == null) return;
        state.messagesByDocument[String(documentId)] = {
          status: "ready",
          items: Array.isArray(items) ? items : [],
          error: null,
        };
      })
      .addCase(fetchInboxMessages.rejected, (state, action) => {
        const documentId = action.payload?.documentId;
        if (documentId == null) return;
        const key = String(documentId);
        if (!state.messagesByDocument[key]) {
          state.messagesByDocument[key] = { ...FROZEN_EMPTY_MESSAGE_SLOT };
        }
        state.messagesByDocument[key].status = "failed";
        state.messagesByDocument[key].error =
          action.payload?.message || "Failed to load messages";
      })
      .addCase(createInboxMessageThunk.pending, (state, action) => {
        setMutationStatus(state, action.meta.arg?.documentId, "message", "loading");
      })
      .addCase(createInboxMessageThunk.fulfilled, (state, action) => {
        const { documentId, message } = action.payload || {};
        if (documentId != null && message) {
          const key = String(documentId);
          if (!state.messagesByDocument[key]) {
            state.messagesByDocument[key] = { ...FROZEN_EMPTY_MESSAGE_SLOT };
          }
          state.messagesByDocument[key].items = [
            message,
            ...state.messagesByDocument[key].items,
          ];
        }
        setMutationStatus(state, documentId, "message", null);
      })
      .addCase(createInboxMessageThunk.rejected, (state, action) => {
        setMutationStatus(state, action.payload?.documentId, "message", null);
      })
      .addCase(markInboxMessageReadThunk.pending, (state, action) => {
        setItemMutationStatus(
          state.messageItemMutations,
          action.meta.arg?.messageId,
          "loading"
        );
      })
      .addCase(markInboxMessageReadThunk.fulfilled, (state, action) => {
        const { documentId, message } = action.payload || {};
        if (message?.id != null) {
          setItemMutationStatus(state.messageItemMutations, message.id, null);
        }
        if (documentId == null || !message?.id) return;
        const key = String(documentId);
        const slot = state.messagesByDocument[key];
        if (slot) {
          const idx = slot.items.findIndex((m) => m?.id === message.id);
          if (idx >= 0) {
            const prev = slot.items[idx];
            slot.items[idx] = message;
            // If the message was previously unread for the recipient,
            // decrement the row's unread_message_count.
            if (!prev.read_at && message.read_at) {
              const row = state.list.items.find(
                (r) => String(r?.id) === String(documentId)
              );
              if (row && (row.unread_message_count || 0) > 0) {
                patchListItem(state, documentId, {
                  unread_message_count: row.unread_message_count - 1,
                });
              }
            }
          }
        }
      })
      .addCase(markInboxMessageReadThunk.rejected, (state, action) => {
        setItemMutationStatus(
          state.messageItemMutations,
          action.payload?.messageId,
          null
        );
      })
      .addCase(fetchDeadlineCards.pending, (state) => {
        state.deadlineCards.status = "loading";
        state.deadlineCards.error = null;
      })
      .addCase(fetchDeadlineCards.fulfilled, (state, action) => {
        // Drop cards already actioned this session: a fetch that was
        // in flight when the user confirmed/dismissed would otherwise
        // resurrect the card after its success toast (Bugbot #304).
        // Server-side the card left `status=proposed` anyway, so this
        // filter only matters for that in-flight window.
        const resolved = new Set(state.resolvedDeadlineCardIds || []);
        const items = Array.isArray(action.payload?.items)
          ? action.payload.items
          : [];
        const kept = items.filter((c) => !resolved.has(String(c.id)));
        const total =
          typeof action.payload?.total === "number"
            ? action.payload.total
            : kept.length;
        state.deadlineCards = {
          status: "ready",
          items: kept,
          // Discount the cards this session already actioned so the
          // header doesn't keep counting deadlines the user just
          // cleared, but never let it drop below what's on screen.
          total: Math.max(total - (items.length - kept.length), kept.length),
          error: null,
        };
      })
      .addCase(fetchDeadlineCards.rejected, (state, action) => {
        state.deadlineCards.status = "failed";
        state.deadlineCards.error =
          action.payload?.message || "Failed to load deadline cards";
      })
      .addCase(confirmDeadlineCardThunk.pending, (state, action) => {
        setItemMutationStatus(
          state.deadlineCardMutations,
          action.meta.arg?.cardId,
          "loading"
        );
      })
      .addCase(confirmDeadlineCardThunk.fulfilled, (state, action) => {
        setItemMutationStatus(
          state.deadlineCardMutations,
          action.payload?.cardId,
          null
        );
        // The strip shows PROPOSED cards; a confirmed card leaves it —
        // and stays gone even if a slower in-flight fetch lands next.
        rememberResolvedDeadlineCard(state, action.payload?.cardId);
        removeDeadlineCard(state, action.payload?.cardId);
      })
      .addCase(confirmDeadlineCardThunk.rejected, (state, action) => {
        setItemMutationStatus(
          state.deadlineCardMutations,
          action.payload?.cardId,
          null
        );
      })
      .addCase(dismissDeadlineCardThunk.pending, (state, action) => {
        setItemMutationStatus(
          state.deadlineCardMutations,
          action.meta.arg?.cardId,
          "loading"
        );
      })
      .addCase(dismissDeadlineCardThunk.fulfilled, (state, action) => {
        setItemMutationStatus(
          state.deadlineCardMutations,
          action.payload?.cardId,
          null
        );
        rememberResolvedDeadlineCard(state, action.payload?.cardId);
        removeDeadlineCard(state, action.payload?.cardId);
      })
      .addCase(dismissDeadlineCardThunk.rejected, (state, action) => {
        setItemMutationStatus(
          state.deadlineCardMutations,
          action.payload?.cardId,
          null
        );
      })

      .addCase(dismissInboxDuplicateThunk.pending, (state, action) => {
        setMutationStatus(
          state,
          action.meta.arg?.documentId,
          "duplicate",
          "loading"
        );
      })
      .addCase(dismissInboxDuplicateThunk.fulfilled, (state, action) => {
        const { documentId, matchId } = action.payload || {};
        // The row stays in the inbox — dismissing the flag says the two
        // documents are different, not that this one has been handled.
        patchListItem(state, documentId, {
          duplicate_flag: null,
          dismissed_duplicate_match_id: matchId ?? null,
        });
        setMutationStatus(state, documentId, "duplicate", null);
      })
      .addCase(dismissInboxDuplicateThunk.rejected, (state, action) => {
        setMutationStatus(
          state,
          action.payload?.documentId,
          "duplicate",
          null
        );
      })

      .addCase(restoreInboxDuplicateThunk.pending, (state, action) => {
        setMutationStatus(
          state,
          action.meta.arg?.documentId,
          "duplicate",
          "loading"
        );
      })
      .addCase(restoreInboxDuplicateThunk.fulfilled, (state, action) => {
        const { documentId, duplicateFlag } = action.payload || {};
        patchListItem(state, documentId, {
          duplicate_flag: duplicateFlag,
          dismissed_duplicate_match_id: null,
        });
        setMutationStatus(state, documentId, "duplicate", null);
      })
      .addCase(restoreInboxDuplicateThunk.rejected, (state, action) => {
        setMutationStatus(
          state,
          action.payload?.documentId,
          "duplicate",
          null
        );
      });
  },
});

export const {
  setSelectedInboxDocument,
  clearInboxList,
  clearInboxDocumentCaches,
} = inboxSlice.actions;

// ---------------------------------------------------------------------------
// Selectors
// ---------------------------------------------------------------------------

const FROZEN_EMPTY_LIST = Object.freeze([]);

export const selectInboxList = (state) =>
  state.inbox?.list || initialListState;

export const selectInboxItems = (state) =>
  state.inbox?.list?.items || FROZEN_EMPTY_LIST;

export const selectInboxListStatus = (state) =>
  state.inbox?.list?.status || "idle";

export const selectInboxListError = (state) =>
  state.inbox?.list?.error || null;

/**
 * Pagination metadata for "Page N of M" UI. Memoized — re-renders only
 * when one of the underlying values changes, so the pagination bar
 * doesn't re-render every poll tick.
 */
export const selectInboxPagination = createSelector(
  [
    (state) => state.inbox?.list?.page || 1,
    (state) => state.inbox?.list?.pageSize || 20,
    (state) => state.inbox?.list?.count || 0,
    (state) => state.inbox?.list?.next || null,
    (state) => state.inbox?.list?.previous || null,
  ],
  (page, pageSize, count, next, previous) => ({
    page,
    pageSize,
    count,
    next,
    previous,
    hasNext: Boolean(next),
    hasPrevious: Boolean(previous),
    totalPages: Math.max(1, Math.ceil(count / Math.max(1, pageSize))),
  })
);

export const selectInboxFilters = createSelector(
  [
    (state) => state.inbox?.list?.matterId ?? null,
    (state) => Boolean(state.inbox?.list?.includeSnoozed),
    (state) => Boolean(state.inbox?.list?.recentlyReviewed),
    (state) => state.inbox?.list?.category || null,
  ],
  (matterId, includeSnoozed, recentlyReviewed, category) => ({
    matterId,
    includeSnoozed,
    recentlyReviewed,
    category,
  })
);

export const selectInboxCategoryCounts = (state) =>
  state.inbox?.list?.categoryCounts || FROZEN_EMPTY_LIST;

/**
 * Args for a list refetch that keeps the attorney's current filters
 * (matter, snooze, reviewed, filing folder) and steps back a page if
 * the last row on this page just left.
 */
export const inboxListFetchArgs = (state) => {
  const filters = selectInboxFilters(state);
  const pagination = selectInboxPagination(state);
  const itemsLeft = state.inbox?.list?.items?.length ?? 0;
  return {
    matterId: filters.matterId,
    includeSnoozed: filters.includeSnoozed,
    recentlyReviewed: filters.recentlyReviewed,
    category: filters.category || null,
    page:
      itemsLeft === 0 && pagination.page > 1
        ? pagination.page - 1
        : pagination.page,
    pageSize: pagination.pageSize,
  };
};

export const inboxIdsInRange = (items, fromId, toId) => {
  const list = Array.isArray(items) ? items : [];
  const fromIdx = list.findIndex(
    (item) => String(item?.id) === String(fromId)
  );
  const toIdx = list.findIndex((item) => String(item?.id) === String(toId));
  if (fromIdx === -1 && toIdx === -1) {
    return toId != null ? [toId] : [];
  }
  if (fromIdx === -1) return toId != null ? [toId] : [];
  if (toIdx === -1) return fromId != null ? [fromId] : [];
  const start = Math.min(fromIdx, toIdx);
  const end = Math.max(fromIdx, toIdx);
  return list.slice(start, end + 1).map((item) => item.id);
};

export const selectSelectedInboxDocumentId = (state) =>
  state.inbox?.selectedDocumentId ?? null;

/**
 * Resolve the full inbox item for the currently-selected document. Used
 * by the right-rail header and the document viewer middle column. Not
 * memoized because it's an O(N) scan of a small list (page-size ≤ 100).
 */
export const selectSelectedInboxItem = (state) => {
  const id = state.inbox?.selectedDocumentId;
  if (id == null) return null;
  const items = state.inbox?.list?.items || FROZEN_EMPTY_LIST;
  // String comparison: ids are UUIDs post split-schema; Number()
  // coercion made every comparison NaN === NaN -> false.
  return items.find((item) => String(item?.id) === String(id)) || null;
};

/**
 * The document the viewer should advance to after the current one
 * (or a selected block) is marked reviewed.
 *
 * Outlook waterfall: the next unread letter BELOW the last one
 * leaving. If nothing is left below, the unread letter immediately
 * above. Never wraps to the top of the page — that is how "mark
 * reviewed" used to yank the attorney back to Kyle Roberts when they
 * were halfway down a pile of Fee Allocation Notices.
 *
 * Call this BEFORE the fulfilled reducer removes the rows. Once they
 * are gone the list no longer knows what sat underneath.
 */
export const selectNextInboxDocumentId = (state, leavingIds) => {
  const leaving = new Set(
    (Array.isArray(leavingIds) ? leavingIds : [leavingIds])
      .filter((id) => id != null && id !== "")
      .map(String)
  );
  const items = state.inbox?.list?.items || FROZEN_EMPTY_LIST;
  if (!items.length || leaving.size === 0) return null;

  let firstLeavingIdx = -1;
  let lastLeavingIdx = -1;
  items.forEach((item, idx) => {
    if (leaving.has(String(item?.id))) {
      if (firstLeavingIdx === -1) firstLeavingIdx = idx;
      lastLeavingIdx = idx;
    }
  });
  if (lastLeavingIdx === -1) return null;

  const isKeep = (item) =>
    item && !item.is_read && !leaving.has(String(item.id));

  const below = items.slice(lastLeavingIdx + 1).find(isKeep);
  if (below) return below.id;
  const above = [...items.slice(0, firstLeavingIdx)].reverse().find(isKeep);
  return above ? above.id : null;
};

/**
 * Among `leavingIds`, which letters sit in this inbox because the reader
 * covers someone else's mail (inbox delegation), and whose? Drives the
 * "Remove from Chris Stratton's mailbox?" confirmation: `count` is how
 * many of the leaving letters belong to a covered person, `names` the
 * distinct people. Letters not on the current page are unknown and are
 * treated as the reader's own.
 */
export const selectInboxCoveredLetters = (state, leavingIds) => {
  const leaving = new Set(
    (Array.isArray(leavingIds) ? leavingIds : [leavingIds])
      .filter((id) => id != null && id !== "")
      .map(String)
  );
  const items = state.inbox?.list?.items || FROZEN_EMPTY_LIST;
  const names = [];
  let count = 0;
  items.forEach((item) => {
    if (!leaving.has(String(item?.id))) return;
    const people = Array.isArray(item?.covering_for) ? item.covering_for : [];
    const labels = people
      .map((p) => (p?.name || p?.username || "").trim())
      .filter(Boolean);
    if (!labels.length) return;
    count += 1;
    labels.forEach((label) => {
      if (!names.includes(label)) names.push(label);
    });
  });
  return { count, names };
};

export const selectInboxUnreadCount = (state) =>
  state.inbox?.unreadCount?.value || 0;

export const selectInboxUnreadCountStatus = (state) =>
  state.inbox?.unreadCount?.status || "idle";

// ---------------------------------------------------------------------------
// Mutation / per-doc selectors (Phase 5b)
// ---------------------------------------------------------------------------

const FROZEN_EMPTY_MUTATIONS = Object.freeze({});

/**
 * Per-doc mutation flags: `{ readState, snooze, task, message }` each
 * being `"loading"` while in flight or undefined when idle. Action
 * footer buttons read these to drive disabled / spinner states.
 */
export const selectInboxMutationsForDocument = (state, documentId) => {
  const key = documentId == null ? "__standalone__" : String(documentId);
  return state.inbox?.mutations?.[key] || FROZEN_EMPTY_MUTATIONS;
};

export const selectInboxTasksForDocument = (state, documentId) => {
  if (documentId == null) return FROZEN_EMPTY_TASK_SLOT;
  return state.inbox?.tasksByDocument?.[String(documentId)] || FROZEN_EMPTY_TASK_SLOT;
};

export const selectInboxMessagesForDocument = (state, documentId) => {
  if (documentId == null) return FROZEN_EMPTY_MESSAGE_SLOT;
  return (
    state.inbox?.messagesByDocument?.[String(documentId)] ||
    FROZEN_EMPTY_MESSAGE_SLOT
  );
};

/**
 * Item-level mutation flags (Phase 5c). Keys are the entity IDs
 * (taskId / messageId) so right-rail panels can drive per-row
 * disabled/spinner states without a doc-wide gate.
 *
 * Returns `"loading"` while the matching PATCH/mark-read is in
 * flight and `undefined` otherwise.
 */
export const selectInboxTaskMutationStatus = (state, taskId) => {
  if (taskId == null) return undefined;
  return state.inbox?.taskItemMutations?.[String(taskId)];
};

export const selectInboxMessageMutationStatus = (state, messageId) => {
  if (messageId == null) return undefined;
  return state.inbox?.messageItemMutations?.[String(messageId)];
};

// ---------------------------------------------------------------------------
// Deadline-card selectors
// ---------------------------------------------------------------------------

export const selectDeadlineCards = (state) =>
  state.inbox?.deadlineCards?.items ?? FROZEN_EMPTY_LIST;

/**
 * Size of the WHOLE proposed-deadline queue, which can be larger than
 * `selectDeadlineCards` — the BE returns a capped page.
 */
export const selectDeadlineCardsTotal = (state) =>
  state.inbox?.deadlineCards?.total ??
  (state.inbox?.deadlineCards?.items?.length || 0);

export const selectDeadlineCardsStatus = (state) =>
  state.inbox?.deadlineCards?.status ?? "idle";

export const selectDeadlineCardsError = (state) =>
  state.inbox?.deadlineCards?.error ?? null;

/**
 * `"loading"` while a confirm/dismiss is in flight for this card,
 * `undefined` otherwise — drives per-card button disabled/spinner
 * state so acting on one card never grays out its neighbors.
 */
export const selectDeadlineCardMutationStatus = (state, cardId) => {
  if (cardId == null) return undefined;
  return state.inbox?.deadlineCardMutations?.[String(cardId)];
};

export default inboxSlice.reducer;
